// Pain-site grouping shared by the Pain page's Sites + Map tabs and the
// dashboard PainWidget (PAIN_TRACKING_PLAN.md Phase 5).
//
// A "site" is one distinct pain location: a group of symptom / headache events
// sharing the D-PAIN-4 site-aware identity key. Grouping goes through
// `symptomIdentityOf` (the single place an event becomes a follow-up identity),
// so a bodyMap-tagged pain never merges with a differently-located one that
// happens to share a name, while every legacy event keys byte-identically to
// before the Pain page shipped.
//
// React-free on purpose so it can be unit-checked and imported anywhere.

import type { MedicalEvent, OpenSymptom, PainBodyMap, PainMark, PainView } from "@medassist/shared";
import { symptomIdentityOf } from "@medassist/shared";

export interface PainSite {
  /** D-PAIN-4 site-aware follow-up key (base key, or `base|siteId` when sited). */
  key: string;
  /** Present once the site has at least one bodyMap-tagged event. */
  siteId?: string;
  /** Display name as first reported ("pain", "headache", a user phrase). */
  name: string;
  /** Optional user-supplied site label from the bodyMap. */
  label?: string;
  region?: string;
  side?: string;
  eventType: "symptom" | "headache";
  /** View to render the thumbnail in — the first mark's view, or "front". */
  primaryView: PainView;
  /** Marks from the site's most recent bodyMap-tagged event. */
  marks: PainMark[];
  radiation?: PainBodyMap["radiation"];
  /** Coordinate frame of the displayed latest payload; absent legacy data is v1. */
  artworkVersion?: PainBodyMap["artworkVersion"];
  /** All events in the group, oldest first. */
  events: MedicalEvent[];
  latestSeverity: number;
  lastUpdatedAt: string;
  firstReportedAt: string;
  /** Last <=10 severity points, newest first (SymptomHistoryPoint shape). */
  history: { at: string; severity: number }[];
}

function severityOf(e: MedicalEvent): number {
  const s = (e.payload as { severity?: unknown }).severity;
  return typeof s === "number" ? s : 0;
}

/**
 * Group a patient's symptom + headache events into active pain sites.
 * Only unresolved groups are returned (latest event severity >= 1 and no
 * `resolvedAt`), sorted most-recently-updated first.
 */
export function groupPainSites(events: MedicalEvent[]): PainSite[] {
  const sorted = [...events]
    .filter((e) => !e.deleted && (e.eventType === "symptom" || e.eventType === "headache"))
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

  const groups = new Map<string, PainSite>();
  for (const e of sorted) {
    const id = symptomIdentityOf(e);
    if (!id) continue;
    const bm = (e.payload as { bodyMap?: PainBodyMap }).bodyMap;
    let g = groups.get(id.key);
    if (!g) {
      g = {
        key: id.key,
        name: id.name,
        eventType: id.eventType,
        primaryView: "front",
        marks: [],
        events: [],
        latestSeverity: 0,
        lastUpdatedAt: e.occurredAt,
        firstReportedAt: e.occurredAt,
        history: [],
      };
      groups.set(id.key, g);
    }
    g.events.push(e);
    if (id.region) g.region = id.region;
    if (id.side) g.side = id.side;
    if (bm?.siteId) g.siteId = bm.siteId;
    if (bm?.label) g.label = bm.label;
    if (bm?.marks?.length) {
      g.marks = bm.marks;
      g.primaryView = bm.marks[0]?.view ?? "front";
      g.radiation = bm.radiation;
      g.artworkVersion = bm.artworkVersion;
    }
  }

  const out: PainSite[] = [];
  for (const g of groups.values()) {
    const latest = g.events[g.events.length - 1];
    if (!latest) continue;
    const lp = latest.payload as { severity?: unknown; resolvedAt?: unknown };
    const latestSeverity = typeof lp.severity === "number" ? lp.severity : 0;
    if (latestSeverity < 1 || typeof lp.resolvedAt === "string") continue;
    g.latestSeverity = latestSeverity;
    g.lastUpdatedAt = latest.occurredAt;
    g.firstReportedAt = g.events[0]!.occurredAt;
    g.history = g.events
      .slice(-10)
      .reverse()
      .map((ev) => ({ at: ev.occurredAt, severity: severityOf(ev) }));
    out.push(g);
  }
  out.sort((a, b) => b.lastUpdatedAt.localeCompare(a.lastUpdatedAt));
  return out;
}

/** Adapt a PainSite to the OpenSymptom shape SymptomFollowUpCard expects.
 *  Reminder/snooze fields are inert here — the Sites tab hides both controls. */
export function siteToOpenSymptom(site: PainSite): OpenSymptom {
  return {
    key: site.key,
    name: site.name,
    region: site.region,
    side: site.side,
    eventType: site.eventType,
    currentSeverity: site.latestSeverity,
    lastUpdatedAt: site.lastUpdatedAt,
    firstReportedAt: site.firstReportedAt,
    history: site.history,
    dueForCheckIn: false,
    reminderIntervalHours: 6,
    nextDueAt: site.lastUpdatedAt,
    siteId: site.siteId,
  };
}
